import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { LocalizationPipe } from '@abp/ng.core';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { FormActionsComponent } from 'src/core/forms/form-actions/form-actions.component';
import { MultiSelectModule } from 'primeng/multiselect';
import { CrudService } from 'src/core/services/crud/crud.service';
import { IvrAssignedTenantDto } from '../../models/ivr-assigned-tenant.dto';
import { IvrInstanceDto } from '../../models/ivr-instance.dto';
import { IvrTenantLookupDto } from '../../models/ivr-tenant-lookup.dto';

@Component({
  selector: 'app-ivr-instance-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgxValidateCoreModule,
    LocalizationPipe,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    MultiSelectModule,
    FormActionsComponent,
  ],
  templateUrl: './ivr-instance-form.component.html',
  styleUrls: ['./ivr-instance-form.component.scss'],
})
export class IvrInstanceFormComponent implements OnInit, OnChanges {
  private readonly crudService = inject(CrudService);
  private readonly fb = inject(FormBuilder);

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<boolean>();
  @Input() isEdit = false;
  @Input() instance: IvrInstanceDto | null = null;

  dialogVisible = false;
  tenants: IvrTenantLookupDto[] = [];
  selectedTenantIds: string[] = [];
  submitting = false;

  form: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(100)]],
    ipAddress: ['', [Validators.required]],
    port: [null, [Validators.required, Validators.min(1), Validators.max(65535)]],
  });

  ngOnInit(): void {
    this.dialogVisible = !!this.visible;
    this.crudService
      .getSimple<IvrTenantLookupDto[]>(IvrInstanceDto, null, 'tenant-lookup')
      .subscribe({
        next: list => (this.tenants = list ?? []),
      });
    this.applyInstanceToForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      this.dialogVisible = !!this.visible;
    }
    if (changes['instance'] || changes['isEdit']) {
      this.applyInstanceToForm();
    }
  }

  private applyInstanceToForm(): void {
    if (!this.form) {
      return;
    }
    if (this.isEdit && this.instance) {
      this.form.patchValue({
        name: this.instance.name,
        ipAddress: this.instance.ipAddress,
        port: this.instance.port,
      });
      const at = this.instance.assignedTenants;
      this.selectedTenantIds = Array.isArray(at) ? at.map(t => t.tenantId) : [];
    } else {
      this.form.reset({ name: '', ipAddress: '', port: null });
      this.selectedTenantIds = [];
    }
  }

  get titleKey(): string {
    return this.isEdit
      ? 'FreeswitchIntegration::EditIvrInstance'
      : 'FreeswitchIntegration::AddIvrInstance';
  }

  get submitLabelKey(): string {
    return this.isEdit ? 'FreeswitchIntegration::Update' : 'FreeswitchIntegration::Save';
  }

  get selectedTenants(): IvrTenantLookupDto[] {
    return this.tenants.filter(t => this.selectedTenantIds.includes(t.id));
  }

  trackByTenantId(_index: number, tenant: IvrTenantLookupDto): string {
    return tenant.id;
  }

  removeTenant(tenantId: string): void {
    this.selectedTenantIds = this.selectedTenantIds.filter(id => id !== tenantId);
  }

  onHide(): void {
    this.visibleChange.emit(false);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { name, ipAddress, port } = this.form.value;
    const assignedTenants: IvrAssignedTenantDto[] = this.selectedTenantIds.map(tenantId => ({
      tenantId,
      ivrInstanceId: this.instance?.id,
    }));

    const input: Partial<IvrInstanceDto> = {
      id: this.isEdit && this.instance ? this.instance.id : undefined,
      name: (name as string).trim(),
      ipAddress: (ipAddress as string).trim(),
      port: port as number,
      isActive: true,
      assignedTenants,
    };

    this.submitting = true;

    if (this.isEdit && this.instance) {
      this.crudService
        .update<void>(IvrInstanceDto, { id: this.instance.id, ...input })
        .pipe(finalize(() => (this.submitting = false)))
        .subscribe({
          next: () => {
            this.saved.emit(false);
            this.visibleChange.emit(false);
          },
          error: () => {},
        });
    } else {
      this.crudService
        .insertWithoutId(IvrInstanceDto, input)
        .pipe(finalize(() => (this.submitting = false)))
        .subscribe({
          next: () => {
            this.saved.emit(true);
            this.visibleChange.emit(false);
          },
          error: () => {},
        });
    }
  }

  cancel(): void {
    this.visibleChange.emit(false);
  }
}
